import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var31');

            const leftImage = new Image();
            leftImage.src = variation.leftImageSrc;
            leftImage.classList.add('image-var31');

            const rightImage = new Image();
            rightImage.src = variation.rightImageSrc;
            rightImage.classList.add('image-var31');

            const leftDiv = document.createElement('div');
            leftDiv.classList.add('left-div-var31');

            const rightDiv = document.createElement('div');
            rightDiv.classList.add('right-div-var31');

            leftDiv.appendChild(leftImage);

            rightDiv.appendChild(rightImage);

            const leftActualPhoto = createWatermarkTagImage(
              'left-actual-photo-var31',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (leftActualPhoto) {
              leftDiv.appendChild(leftActualPhoto);
            }

            const rightActualPhoto = createWatermarkTagImage(
              'right-actual-photo-var31',
              variation.rightWatermarkTag,
              variation.rightWatermarkPosition,
            );
            if (rightActualPhoto) {
              rightDiv.appendChild(rightActualPhoto);
            }

            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');

              middleBreak.classList.add('break-var31');

              if (variation.divider === 'black') {
                middleBreak.classList.add('break-var31--black');
              }

              imageHolder.appendChild(middleBreak);
            }

            if (variation.leftText) {
              const leftText = document.createElement('div');
              leftText.classList.add('left-text-var31');

              if (variation.divider !== 'none') {
                leftText.classList.add('left-text-var31__divider');
              }

              const leftP = document.createElement('p');
              const updatedText = replaceQuotes(variation.leftText);
              let text = '';
              if (variation.leftQuotes) {
                text = `“${updatedText}”`;
              } else {
                text = updatedText;
              }

              if (variation.leftBreakes) {
                leftP.innerText = text;
                leftP.classList.add('custom-brakes-var31');
              } else {
                leftP.innerHTML = distributeTextCanvas(text, {
                  maxWidth: 510,
                  fontFamily: 'CustomArial',
                  minFontSize: 45,
                });
              }

              leftText.appendChild(leftP);

              leftDiv.appendChild(leftText);
            }

            if (variation.rightText) {
              const rightText = document.createElement('div');
              rightText.classList.add('right-text-var31');

              if (variation.divider !== 'none') {
                rightText.classList.add('right-text-var31__divider');
              }

              const rightP = document.createElement('p');
              const updatedText = replaceQuotes(variation.rightText);
              let text = '';
              if (variation.rightQuotes) {
                text = `“${updatedText}”`;
              } else {
                text = updatedText;
              }

              if (variation.rightBreakes) {
                rightP.innerText = text;
                rightP.classList.add('custom-brakes-var31');
              } else {
                rightP.innerHTML = distributeTextCanvas(text, {
                  maxWidth: 510,
                  fontFamily: 'CustomArial',
                  minFontSize: 45,
                });
              }

              rightText.appendChild(rightP);

              rightDiv.appendChild(rightText);
            }

            imageHolder.appendChild(leftDiv);
            imageHolder.appendChild(rightDiv);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var31',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
